import { TrustCircleListingBanner } from './trust-circle-listing-banner';
import { useListingCatalog } from 'app/hooks/use-listing-catalog';
import { GeoPin } from 'app/design/icons/geo-pin';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { IconEyeOff } from '@tabler/icons-react-native';
import { useDistanceUnit } from 'app/hooks/use-distance-unit';
import { useListingCard } from 'app/hooks/use-listing-card';
import { getListingCatalogLabels } from 'app/lib/listing-catalog-labels';
import { BonificationBadge } from 'app/screens/billboard/all-listings/components/bonification-banner';
import { Checkbox, createTheme as createDSTheme } from 'app/shopify';
import { Listing, ListingSubtype, ListingType } from 'app/types/listing';
import type { ListingOfferCardProps } from 'app/types/listing-offer-card-props';
import { WhiteLabel } from 'app/types/white-labels';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View, type DimensionValue, type ViewStyle } from 'react-native';
import { CorpoImage } from './corp-image';
import { FavouriteButton } from './favourite-button';
import { ListingCardAuthorStats } from './listing-card-author-stats';
import { LoanFeeTag } from './listing-card-loan-fee-tag';
import {
  ListingCardModeBadge,
  ListingCardPerishableIcon,
  ListingCardPrice,
  ListingCardTimeTag,
} from './listing-card-metadata';
import { ListingPostedByMembers } from './listing-card-posted-by-members';
import { ListingThumbnail } from './listing-thumbnail';
const theme = createDSTheme();

type ListingOfferCardVariant = 'small' | 'large';
type ListingOfferCardPresentation = 'default' | 'recommended-carousel';

interface ListingOfferCardBaseProps extends ListingOfferCardProps {
  presentation?: ListingOfferCardPresentation;
  variant: ListingOfferCardVariant;
}

interface VariantConfig {
  containerStyle: ViewStyle;
  contentGap: 'spacing.general.x1' | 'spacing.general.x2' | 'spacing.general.x4';
  rootMinWidth: number | undefined;
  selectionHeight: number;
  imageHeight: number | undefined;
  imageWidth: DimensionValue;
  imageAspectRatio: number | undefined;
  thumbnailProps: { width: DimensionValue } | { size: number };
  tagsRowStyle: ViewStyle;
  tagsRowGap: 'spacing.general.x1';
  tagCountStyle: ViewStyle;
  tagFontSize: 11 | 12;
  tagPaddingVertical: 'spacing.general.x0,5' | 'spacing.general.x1';
  primaryTagTextColor: 'categoryPrimary' | 'text.primary';
  showMetaRow: boolean;
  avatarSize: number;
  titleFontSize: number;
  titleLineHeight?: number;
  titleNumberOfLines: number;
}

export const LARGE_SIZE = 180;

function getVariantConfig(
  variant: ListingOfferCardVariant,
  columnWidth?: number,
  presentation: ListingOfferCardPresentation = 'default',
): VariantConfig {
  const isResponsiveGrid = columnWidth !== undefined;

  if (variant === 'small') {
    const isRecommendedCarousel = presentation === 'recommended-carousel';

    return {
      containerStyle: {
        flex: 1,
        width: isResponsiveGrid ? ('100%' as const) : undefined,
        maxWidth: isResponsiveGrid ? undefined : isRecommendedCarousel ? 158 : 180,
        paddingHorizontal: 0,
        marginVertical: isRecommendedCarousel ? 0 : 8,
      },
      contentGap: 'spacing.general.x2' as const,
      rootMinWidth: isResponsiveGrid ? undefined : isRecommendedCarousel ? 158 : 180,
      selectionHeight: 100,
      imageHeight: isRecommendedCarousel ? 158 : 100,
      imageWidth: '100%' as const,
      imageAspectRatio: 1,
      thumbnailProps: { width: '100%' as const },
      tagsRowStyle: { rowGap: theme.spacing['spacing.general.x2'] },
      tagsRowGap: 'spacing.general.x1' as const,
      tagCountStyle: { backgroundColor: theme.colors['surface.tertiary-neutral'] },
      tagFontSize: isRecommendedCarousel ? 11 : 12,
      tagPaddingVertical: isRecommendedCarousel ? 'spacing.general.x0,5' : 'spacing.general.x1',
      primaryTagTextColor: 'categoryPrimary' as const,
      showMetaRow: false,
      avatarSize: isRecommendedCarousel ? 26 : 30,
      titleFontSize: isRecommendedCarousel ? 16 : 14,
      titleLineHeight: isRecommendedCarousel ? 20 : undefined,
      titleNumberOfLines: 2,
    };
  }

  return {
    containerStyle: {
      width: isResponsiveGrid ? ('100%' as const) : undefined,
      marginVertical: theme.spacing['spacing.general.x4'],
      maxWidth: isResponsiveGrid ? undefined : LARGE_SIZE,
    },
    contentGap: 'spacing.general.x2' as const,
    rootMinWidth: isResponsiveGrid ? undefined : LARGE_SIZE,
    selectionHeight: columnWidth ?? LARGE_SIZE,
    imageHeight: isResponsiveGrid ? undefined : LARGE_SIZE,
    imageWidth: '100%' as const,
    imageAspectRatio: isResponsiveGrid ? 1 : undefined,
    thumbnailProps: isResponsiveGrid ? { width: '100%' as const } : { size: LARGE_SIZE },
    tagsRowStyle: { rowGap: theme.spacing['spacing.general.x2'] },
    tagsRowGap: 'spacing.general.x1' as const,
    tagCountStyle: { backgroundColor: theme.colors['surface.tertiary-neutral'] },
    tagFontSize: 12,
    tagPaddingVertical: 'spacing.general.x1',
    primaryTagTextColor: 'text.primary' as const,
    showMetaRow: true,
    avatarSize: 30,
    titleFontSize: 14,
    titleNumberOfLines: 2,
  };
}

const ListingOfferCardBase = memo(function ListingOfferCardBaseComponent(props: ListingOfferCardBaseProps) {
  const {
    continueTutorial,
    hideCorpoBadge = false,
    isFirst,
    isNew,
    isSelecting,
    listing,
    onPress: onCardPress,
    disableDefaultPress,
    selectedRowsState,
    tutorialViewRef: firstOfferTutorialViewRef,
    variant,
    presentation,
    columnWidth,
  } = props;
  const { t } = useTranslation('all-listings');
  const resolvedColumnWidth = typeof columnWidth === 'number' ? columnWidth : undefined;
  const config = getVariantConfig(variant, resolvedColumnWidth, presentation);

  const { categoryPrimary, item, type, subtype, before, groups, distance } = listing;

  const {
    isAuthor,
    isInvisible,
    myUser,
    tutorialViewRef,
    isSelected,
    displayNumViews,
    displayNumFavourites,
    displayTrustPoints,
    thumbPicture,
    onPress,
    loanFeeAvailable,
  } = useListingCard({
    continueTutorial,
    isFirst,
    isSelecting,
    listing,
    selectedRowsState,
    tutorialViewRef: firstOfferTutorialViewRef,
  });

  const { formatDistanceWithoutText } = useDistanceUnit();
  const hasCorpoBadge = !!(
    groups?.length &&
    !hideCorpoBadge &&
    !isFeatureEnabled(FeatureFlag.justOneClub, myUser?.featureFlags)
  );
  const { featureFlags } = useListingCatalog();
  const catalogLabels = getListingCatalogLabels(listing, featureFlags);
  const hasTags = catalogLabels.length > 0;
  const firstCatalogLabel = catalogLabels[0]?.label;
  const additionalTagsCount = Math.max(0, catalogLabels.length - 1);
  const shouldInlineDistanceWithTags = distance !== undefined;
  const listingPostedByUser = useMemo(
    () => ({ ...listing.user, trustPoints: displayTrustPoints }),
    [displayTrustPoints, listing.user],
  );

  const handlePress = useCallback(() => {
    onCardPress?.(listing);
    if (!disableDefaultPress) {
      onPress();
    }
  }, [disableDefaultPress, listing, onCardPress, onPress]);

  const listingCardTestID = `listing-offer-card-${item.name}`;
  return (
    <View ref={tutorialViewRef} style={config.containerStyle}>
      <Column gap={config.contentGap} minWidth={config.rootMinWidth}>
        {!isAuthor ? (
          <Pressable
            onPress={handlePress}
            accessibilityRole={isSelecting ? 'checkbox' : 'button'}
            accessibilityState={isSelecting ? { checked: Boolean(isSelected) } : undefined}
          >
            <ListingPostedByMembers
              avatarSize={loanFeeAvailable ? Math.min(config.avatarSize, 24) : config.avatarSize}
              grouping={listing.grouping}
              isAuthor={false}
              loanFee={listing.loanFee}
              textColor={undefined}
              user={listingPostedByUser}
            />
          </Pressable>
        ) : null}
        <Column position="relative">
          {/* Keep the card action and its favourite control as siblings so web has no nested interactions. */}
          <Pressable
            accessibilityRole={isSelecting ? 'checkbox' : 'button'}
            accessibilityState={isSelecting ? { checked: Boolean(isSelected) } : undefined}
            testID={listingCardTestID}
            onPress={handlePress}
          >
            <Column gap={config.contentGap}>
              <OfferThumbnail
                isInvisible={isInvisible}
                before={before}
                listing={listing}
                thumbPicture={thumbPicture}
                subtype={subtype}
                type={type}
                config={config}
                hasCorpoBadge={hasCorpoBadge}
                groups={groups}
                isAuthor={isAuthor}
                isSelecting={isSelecting}
                isNew={isNew}
              />

              <OfferDetails
                listing={listing}
                isAuthor={isAuthor}
                config={config}
                item={item}
                subtype={subtype}
                hasTags={hasTags}
                firstCatalogLabel={firstCatalogLabel}
                additionalTagsCount={additionalTagsCount}
                shouldInlineDistanceWithTags={shouldInlineDistanceWithTags}
                formatDistanceWithoutText={formatDistanceWithoutText}
                distance={distance}
                loanFeeAvailable={loanFeeAvailable}
                displayNumViews={displayNumViews}
                displayNumFavourites={displayNumFavourites}
                categoryPrimary={categoryPrimary}
                t={t}
              />
            </Column>
          </Pressable>

          {!isAuthor && !isInvisible ? (
            <Row position="absolute" top={6} right={6} backgroundColor="white" borderRadius="radius.full" zIndex={1}>
              <FavouriteButton listing={listing} />
            </Row>
          ) : null}

          {isSelecting ? (
            <View
              pointerEvents="none"
              style={{
                position: 'absolute',
                top: theme.spacing['spacing.general.x2'],
                right: theme.spacing['spacing.general.x2'],
                width: 24,
                height: 24,
                zIndex: 1,
              }}
            >
              <Checkbox selected={Boolean(isSelected)} showText={false} />
            </View>
          ) : null}
        </Column>
      </Column>
    </View>
  );
});

const styles = StyleSheet.create({
  hiddenListingOverlay: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export { ListingOfferCardBase };
export type { ListingOfferCardVariant };

function OfferDetails({
  listing,
  isAuthor,
  config,
  item,
  subtype,
  hasTags,
  firstCatalogLabel,
  additionalTagsCount,
  shouldInlineDistanceWithTags,
  formatDistanceWithoutText,
  distance,
  loanFeeAvailable,
  displayNumViews,
  displayNumFavourites,
  categoryPrimary,
  t,
}: {
  listing: Listing;
  isAuthor: boolean;
  config: ReturnType<typeof getVariantConfig>;
  item: { id: string; name: string };
  subtype: ListingSubtype;
  hasTags: boolean;
  firstCatalogLabel: string | undefined;
  additionalTagsCount: number;
  shouldInlineDistanceWithTags: boolean;
  formatDistanceWithoutText: (distanceInMeters?: number) => string;
  distance: number | undefined;
  loanFeeAvailable: boolean;
  displayNumViews: number | undefined;
  displayNumFavourites: number | undefined;
  categoryPrimary: Listing['categoryPrimary'];
  t: ReturnType<typeof useTranslation<'all-listings'>>['t'];
}) {
  return (
    <Column flex={1} gap="spacing.general.x2">
      {listing.canBonify && isAuthor ? (
        <Row>
          <BonificationBadge />
          <Column flex={1} />
        </Row>
      ) : null}
      <Text
        fontWeight="600"
        numberOfLines={config.titleNumberOfLines}
        fontSize={config.titleFontSize}
        lineHeight={config.titleLineHeight}
      >
        {item.name}
      </Text>

      <OfferTags
        isAuthor={isAuthor}
        config={config}
        listing={listing}
        subtype={subtype}
        hasTags={hasTags}
        firstCatalogLabel={firstCatalogLabel}
        additionalTagsCount={additionalTagsCount}
        shouldInlineDistanceWithTags={shouldInlineDistanceWithTags}
        formatDistanceWithoutText={formatDistanceWithoutText}
        distance={distance}
      />
      {!isAuthor && loanFeeAvailable && listing.loanFee ? <LoanFeeTag loanFee={listing.loanFee} /> : null}
      {!isAuthor ? <ListingCardPrice listing={listing} /> : null}

      <OfferOwnerStats
        isAuthor={isAuthor}
        displayNumViews={displayNumViews}
        displayNumFavourites={displayNumFavourites}
        categoryPrimary={categoryPrimary}
        t={t}
      />
      {isAuthor && loanFeeAvailable && listing.loanFee ? <LoanFeeTag loanFee={listing.loanFee} /> : null}
    </Column>
  );
}

function OfferOwnerStats({
  isAuthor,
  displayNumViews,
  displayNumFavourites,
  categoryPrimary,
  t,
}: {
  isAuthor: boolean;
  displayNumViews: number | undefined;
  displayNumFavourites: number | undefined;
  categoryPrimary: Listing['categoryPrimary'];
  t: ReturnType<typeof useTranslation<'all-listings'>>['t'];
}): React.ReactNode {
  return isAuthor ? (
    Number.isInteger(displayNumViews) && Number.isInteger(displayNumFavourites) ? (
      <ListingCardAuthorStats numViews={displayNumViews} numFavourites={displayNumFavourites} />
    ) : (
      <Row alignItems="center" paddingVertical="spacing.general.x0,5">
        <Row
          alignItems="center"
          backgroundColor="surface.disabled"
          borderRadius="radius.xl"
          paddingHorizontal="spacing.general.x1"
          paddingVertical="spacing.general.x1"
          style={{ backgroundColor: categoryPrimary.colorLight }}
        >
          <Text
            fontSize={12}
            fontWeight="600"
            paddingHorizontal="spacing.general.x1"
            style={{ color: categoryPrimary.colorDark }}
          >
            {t('manageOffer', { ns: 'common' })}
          </Text>
        </Row>
      </Row>
    )
  ) : null;
}

function OfferTags({
  isAuthor,
  config,
  listing,
  subtype,
  hasTags,
  firstCatalogLabel,
  additionalTagsCount,
  shouldInlineDistanceWithTags,
  formatDistanceWithoutText,
  distance,
}: {
  isAuthor: boolean;
  config: ReturnType<typeof getVariantConfig>;
  listing: Listing;
  subtype: ListingSubtype;
  hasTags: boolean;
  firstCatalogLabel: string | undefined;
  additionalTagsCount: number;
  shouldInlineDistanceWithTags: boolean;
  formatDistanceWithoutText: (distanceInMeters?: number) => string;
  distance: number | undefined;
}): React.ReactNode {
  return !isAuthor ? (
    <Row alignItems="center" columnGap={config.tagsRowGap} flexWrap="wrap" style={config.tagsRowStyle}>
      {isWhiteLabel(WhiteLabel.HauteGaspesie) ? <ListingCardModeBadge listing={listing} /> : null}
      {subtype === ListingSubtype.expirable ? <ListingCardPerishableIcon listing={listing} /> : null}
      {hasTags ? (
        <Row
          alignItems="center"
          borderRadius="radius.xl"
          backgroundColor="surface.tertiary-neutral"
          paddingHorizontal="spacing.general.x1"
          paddingVertical={config.tagPaddingVertical}
          maxWidth="100%"
        >
          <Text color="text.secondary" fontSize={config.tagFontSize} numberOfLines={1} fontWeight="500">
            {firstCatalogLabel}
          </Text>
        </Row>
      ) : null}
      {additionalTagsCount > 0 ? (
        <Row
          alignItems="center"
          borderRadius="radius.xl"
          paddingHorizontal="spacing.general.x1"
          paddingVertical={config.tagPaddingVertical}
          style={config.tagCountStyle}
        >
          <Text fontSize={config.tagFontSize} style={{ color: theme.colors['text.secondary'] }}>
            +{additionalTagsCount}
          </Text>
        </Row>
      ) : null}
      {shouldInlineDistanceWithTags ? (
        <Row alignItems="center" style={{ gap: 1 }}>
          <GeoPin color={theme.colors['icon.secondary']} height={12} width={12} />
          <Text fontSize={12} style={{ color: theme.colors['icon.secondary'] }}>
            {formatDistanceWithoutText(distance)}
          </Text>
        </Row>
      ) : null}
    </Row>
  ) : null;
}

function OfferThumbnail({
  isInvisible,
  before,
  listing,
  thumbPicture,
  subtype,
  type,
  config,
  hasCorpoBadge,
  groups,
  isAuthor,
  isSelecting,
  isNew,
}: {
  isInvisible: boolean;
  before: string | null | undefined;
  listing: Listing;
  thumbPicture: string | undefined;
  subtype: ListingSubtype;
  type: ListingType;
  config: ReturnType<typeof getVariantConfig>;
  hasCorpoBadge: boolean;
  groups: Listing['groups'];
  isAuthor: boolean;
  isSelecting: boolean | undefined;
  isNew: boolean | undefined;
}) {
  return (
    <Column borderRadius="radius.md" overflow="hidden">
      <Column position="relative">
        <View style={{ opacity: isInvisible ? 0.3 : 1 }}>
          <ListingThumbnail
            style={listing.isTrustCircleOnly ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : undefined}
            before={before}
            isFound={!!listing.lender}
            picture={thumbPicture}
            skipDonationOverlay
            subtype={subtype}
            type={type}
            {...config.thumbnailProps}
            width={config.imageWidth}
          />
        </View>
        {isInvisible ? (
          <View pointerEvents="none" style={[StyleSheet.absoluteFill, styles.hiddenListingOverlay]}>
            <IconEyeOff strokeWidth={ICON_STROKE_WIDTH} size={44} />
          </View>
        ) : (
          <>
            {hasCorpoBadge && !listing.isTrustCircleOnly ? (
              <Row
                position="absolute"
                bottom={10}
                left={14}
                alignItems="center"
                gap="spacing.general.x1"
                style={{ backgroundColor: 'rgba(255,255,255,0.60)' }}
                borderRadius="radius.lg"
              >
                <CorpoImage picture={groups?.[0]?.picture} />
              </Row>
            ) : null}
            <Row position="absolute" top={12} left={12} right={!isAuthor || isSelecting ? 62 : 12}>
              <ListingCardTimeTag isNew={isAuthor ? false : isNew} listing={listing} />
            </Row>
          </>
        )}
      </Column>
      <TrustCircleListingBanner listing={listing} />
    </Column>
  );
}
